// Pure platform-to-InstallTarget resolution, lifted out of AppInstallService
// so it can be unit-tested without pulling in environment-helpers (which
// transitively imports Angular and Capacitor and can't load under ts-node
// mocha). The service supplies the platform booleans + URLs at call time.
//
// The Android store URL is injected (rather than imported from shared) so the
// service can pick the env-aware value via `androidPlayStoreUrlForEnv` —
// dev.mh-physique.app must recommend the dev Play package. The iOS store URL
// stays env-agnostic; one App Store listing covers every iOS install path.

import { InstallTarget, InstallTargetKind } from './app-install-types';

export interface InstallResolverInputs {
  isCapacitorNative: boolean;
  isPlatformIOS: boolean;
  isAndroidBrowser: boolean;
  hasAndroidNativeInstalled: boolean;
  hasPwaPrompt: boolean;
  universalLinkUrl: string;
  androidStoreUrl: string;
  iosStoreUrl: string;
}

export function resolveInstallTargetFromInputs(inputs: InstallResolverInputs): InstallTarget {
  if (inputs.isCapacitorNative) return { kind: InstallTargetKind.None };

  if (inputs.isPlatformIOS) {
    // #5660: deliberately no openUrl. iOS does not hand a link off to the
    // native app when the link is on the same domain as the page the user is
    // browsing, and universalLinkUrl is our own origin. So the "Open it" link
    // never reached the app — it opened a second browser tab of the web app,
    // which cold started and showed the install sheet again. Users tapped
    // again and saw the app "blinking and flashing". The App Store badge is
    // the affordance that does work on iOS: it leaves our domain, and the
    // store page shows "OPEN" when the app is installed.
    return {
      kind: InstallTargetKind.NativeStore,
      storeUrl: inputs.iosStoreUrl,
      platform: 'ios',
    };
  }

  if (inputs.isAndroidBrowser) {
    if (inputs.hasAndroidNativeInstalled) {
      return {
        kind: InstallTargetKind.NativeOpen,
        openUrl: inputs.universalLinkUrl,
        platform: 'android',
      };
    }
    return {
      kind: InstallTargetKind.NativeStore,
      storeUrl: inputs.androidStoreUrl,
      openUrl: inputs.universalLinkUrl,
      platform: 'android',
    };
  }

  if (inputs.hasPwaPrompt) return { kind: InstallTargetKind.PwaPrompt };
  return { kind: InstallTargetKind.None };
}

// #5707: the startup install sheet can be turned off per browser with its
// "Don't show again" link. The flag lives in localStorage, so it is per
// browser, not per account. Settings → Install App ignores it.
export const APP_INSTALL_STARTUP_OPT_OUT_KEY = 'appInstallStartupSheetOptOut';
const OPTED_OUT_VALUE = '1';

export interface StartupSheetInputs {
  isCapacitorNative: boolean;
  isPlatformIOS: boolean;
  isAndroidBrowser: boolean;
  // called only once the platform could show the sheet, so desktop and the
  // native app never touch storage
  isOptedOut: () => boolean;
}

// True iff mobile web/PWA outside Capacitor — regardless of whether the
// native app is detected as installed (we still nag PWA-only users to
// migrate; on detected-installed Android the sheet collapses to a single
// "Open" CTA so it's not annoying) — unless this browser opted out.
export function shouldShowStartupSheetFromInputs(inputs: StartupSheetInputs): boolean {
  if (inputs.isCapacitorNative) return false;
  if (!inputs.isPlatformIOS && !inputs.isAndroidBrowser) return false;
  return !inputs.isOptedOut();
}

// only the exact value we write counts as opted out, so a damaged or foreign
// value falls back to showing the sheet. Throws if storage throws — the
// caller reports it.
export function readStartupSheetOptOut(storage: Pick<Storage, 'getItem'>): boolean {
  return storage.getItem(APP_INSTALL_STARTUP_OPT_OUT_KEY) === OPTED_OUT_VALUE;
}

// saves this browser's opt-out. Throws if storage throws — the caller reports it.
export function writeStartupSheetOptOut(storage: Pick<Storage, 'setItem'>): void {
  storage.setItem(APP_INSTALL_STARTUP_OPT_OUT_KEY, OPTED_OUT_VALUE);
}
